/**
 * Idle Warning Modal
 * Displays a warning when user is about to be logged out due to inactivity
 */

import { Button } from '@/components/ui/button';
import React from 'react';
import { WarningCircle } from '@phosphor-icons/react';

interface IdleWarningModalProps {
  isOpen: boolean;
  remainingTime: number;
  onStayLoggedIn: () => void;
  onLogoutNow: () => void;
}

const IdleWarningModal: React.FC<IdleWarningModalProps> = ({
  isOpen,
  remainingTime,
  onStayLoggedIn,
  onLogoutNow
}) => {
  if (!isOpen) return null;

  const progressPercentage = (remainingTime / 30) * 100; // 30 seconds warning period

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal */}
      <div className="relative glass-modal max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-3">
            <WarningCircle className="h-8 w-8 text-amber-600 dark:text-amber-500" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
          Session Timeout Warning
        </h2>

        {/* Message */}
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
          You will be logged out due to inactivity in:
        </p>

        {/* Countdown */}
        <div className="text-center mb-6">
          <div className="text-4xl font-bold text-amber-600 dark:text-amber-500 mb-2">
            {remainingTime}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {remainingTime === 1 ? 'second' : 'seconds'}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-1000 ease-linear"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
            <span>Logging out...</span>
            <span>{progressPercentage.toFixed(0)}%</span>
          </div>
        </div>

        {/* Question */}
        <p className="text-gray-700 dark:text-gray-300 text-center mb-6 font-medium">
          Do you want to stay logged in?
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <Button type="button" className="flex-1" onClick={onStayLoggedIn}>
            Stay Logged In
          </Button>
          <Button type="button" variant="outline" className="flex-1" onClick={onLogoutNow}>
            Logout Now
          </Button>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
          Any mouse movement or keyboard input will keep you logged in
        </p>
      </div>
    </div>
  );
};

export default IdleWarningModal;
